import React, { FC } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import {
  HostingVersionCatalogue,
  ReleaseFamily,
  VersionDescriptor
} from '@feedyou-ai/feedbot-schema'
import cx from 'classnames'
import { getAllActiveReleases } from '@features/hostingVersions'

import { FeedbotSelect } from '../../../../../../selects'
import { toSelectOption } from '../../../../../../../../lib/options'
import { Button } from '../../../../../../Button'
import { updateHosting } from '../../../../../../../../actions/bot'
import { useBotId } from '../../../../../../../../store/flow'
import { getVersionOptions } from '../utils/getVersionOptions'
import { FeedbotDispatch } from '../../../../../../../../store/types'
import { useSdkVersionFormState } from '../hooks/useSdkVersionFormState'
import { useAvailableVersionsForFamily } from '../hooks/useAvailableVersionsForFamily'

type Props = {
  versionCatalogue: HostingVersionCatalogue
  deployedVersion: VersionDescriptor
  onSubmit?(): void
}

const EDITED_VERSION_DATA_TEST = 'version-select-edited'
const SELECTED_VERSION_DATA_TEST = 'data-selected-version'

export const ChangeSdkVersionForm: FC<Props> = ({
  versionCatalogue,
  deployedVersion,
  onSubmit
}) => {
  const botId = useBotId()
  const dispatch = useDispatch() as FeedbotDispatch

  const {
    state: { version, releaseFamily },
    updateState
  } = useSdkVersionFormState(versionCatalogue, deployedVersion)

  const latestVersion = versionCatalogue[releaseFamily!]?.latestVersion

  const availableVersions = useAvailableVersionsForFamily(versionCatalogue, releaseFamily)

  const selectedVersionDiffersFromDeployed =
    version && deployedVersion?.version && version !== deployedVersion?.version

  return (
    <>
      <ReleaseSelect
        placeholder="Release"
        value={releaseFamily ? toSelectOption(releaseFamily) : undefined}
        options={getAllActiveReleases(versionCatalogue).map(release => toSelectOption(release))}
        onChange={option =>
          updateState({ releaseFamily: option?.value as ReleaseFamily | undefined })
        }
        isSearchable
      />

      <VersionSelect
        placeholder="Version"
        value={version ? toSelectOption(version) : null}
        options={getVersionOptions(availableVersions, deployedVersion?.version, latestVersion)}
        data-test={cx({
          'version-select': true,
          [EDITED_VERSION_DATA_TEST]: selectedVersionDiffersFromDeployed
        })}
        additionalDataAttributes={{ [SELECTED_VERSION_DATA_TEST]: `${releaseFamily}/${version}` }}
        onChange={option => updateState({ version: option?.value })}
        isSearchable
      />

      <SetButton
        label="Set"
        emphasis="primary"
        disabled={!deployedVersion}
        onClick={() => {
          const selectedVersionObject = availableVersions?.find(
            hostingVersion => hostingVersion.version === version
          )

          dispatch(updateHosting(botId, selectedVersionObject!.file)).then(
            ((success: boolean) => success && onSubmit?.()) as any
          )
        }}
      />
    </>
  )
}

// This is obviously a hack - but a reasonable one, compared to propagating the state to save button the propper way
export function isSdkVersionFormEditedFromDataTest() {
  return !!document.querySelector(`[data-test~=${EDITED_VERSION_DATA_TEST}]`)
}

export function getSelectedVersionFromDataTest() {
  return document
    .querySelector(`[${SELECTED_VERSION_DATA_TEST}]`)
    ?.getAttribute(SELECTED_VERSION_DATA_TEST)
}

export type ChangeSdkVersionFormProps = Props

const ReleaseSelect = styled(FeedbotSelect)`
  width: 105px;
  min-width: 105px;
  margin-right: 8px;
`

const VersionSelect = styled(FeedbotSelect)`
  margin-right: 8px;
`

const SetButton = styled(Button)`
  height: 38px;
`
